import React, { useEffect, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import MoshkyAnalyticsPanel from "@/components/MoshkyAnalyticsPanel";

// ── Types ──────────────────────────────────────────────────────────────────────

interface LeaderboardRow {
  rank: number;
  agentId: number;
  displayName: string;
  picture?: string;
  totalChats: number;
  resolvedChats: number;
  resolutionRate: number;
  avgFirstResponseSec: number;
  avgCsatScore: number;
  qualityScore?: number | null;
}

interface Team {
  id: number;
  name: string;
}

interface AgentQuality {
  agentId: number;
  avgScore: number | null;
}

interface Competition {
  id: number;
  name: string;
  description?: string;
  metric: string;
  startDate: string;
  endDate: string;
  isLive: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week",  label: "This Week" },
  { key: "month", label: "This Month" },
];

const METRICS = [
  { key: "totalChats",          label: "Chats Handled" },
  { key: "resolutionRate",      label: "Resolution %" },
  { key: "avgFirstResponseSec", label: "Response Time" },
  { key: "avgCsatScore",        label: "CSAT" },
];

function medalIcon(rank: number) {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
}

function fmtResponseTime(sec: number) {
  if (sec <= 0) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtCsat(score: number) {
  if (score < 0) return "—";
  return score.toFixed(1);
}

function scoreColor(score: number | null | undefined) {
  if (score == null) return "var(--bs-secondary)";
  if (score >= 80) return "#22c55e";
  if (score >= 60) return "#f59e0b";
  return "#ef4444";
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function Leaderboard() {
  const api = useApi();

  const [rows, setRows]               = useState<LeaderboardRow[]>([]);
  const [teams, setTeams]             = useState<Team[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  const [period,  setPeriod]  = useState("week");
  const [metric,  setMetric]  = useState("totalChats");
  const [teamId,  setTeamId]  = useState(0);
  const [sortCol, setSortCol] = useState<string>(metric);
  const [sortAsc, setSortAsc] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [lb, teamsData, compData, qualData] = await Promise.all([
        api.messaging.get(`Leaderboard?period=${period}&metric=${metric}&teamId=${teamId}`).then(r => r.ok ? r.json() : []).catch(() => []),
        api.messaging.get("Leaderboard/teams").then(r => r.ok ? r.json() : []).catch(() => []),
        api.messaging.get("Leaderboard/competitions").then(r => r.ok ? r.json() : []).catch(() => []),
        api.messaging.get("api/admin/Reports/AgentPerformance?days=30").then(r => r.ok ? r.json() : null).catch(() => null),
      ]);

      // Build quality score map
      const qualMap: Record<number, number | null> = {};
      if (qualData?.agents) {
        for (const a of qualData.agents as AgentQuality[]) {
          qualMap[a.agentId] = a.avgScore;
        }
      }

      const withQuality: LeaderboardRow[] = Array.isArray(lb)
        ? lb.map((r: LeaderboardRow) => ({ ...r, qualityScore: qualMap[r.agentId] ?? null }))
        : [];

      setRows(withQuality);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setCompetitions(Array.isArray(compData) ? compData : []);
    } catch {
      setError("Could not load leaderboard data.");
    } finally {
      setLoading(false);
    }
  }, [api, period, metric, teamId]);

  useEffect(() => { load(); }, [load]);

  // Update sort column when metric changes
  useEffect(() => { setSortCol(metric); setSortAsc(metric === "avgFirstResponseSec"); }, [metric]);

  function toggleSort(col: string) {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(col === "avgFirstResponseSec"); }
  }

  const sorted = [...rows].sort((a, b) => {
    let va: number, vb: number;
    switch (sortCol) {
      case "totalChats":          va = a.totalChats;          vb = b.totalChats;          break;
      case "resolutionRate":      va = a.resolutionRate;      vb = b.resolutionRate;      break;
      case "avgFirstResponseSec": va = a.avgFirstResponseSec; vb = b.avgFirstResponseSec; break;
      case "avgCsatScore":        va = a.avgCsatScore;        vb = b.avgCsatScore;        break;
      case "qualityScore":        va = a.qualityScore ?? -1;  vb = b.qualityScore ?? -1;  break;
      default:                    return 0;
    }
    return sortAsc ? va - vb : vb - va;
  });

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <i className="fa fa-sort text-slate-300 ml-1" style={{ fontSize: 10 }} />;
    return <i className={`fa fa-sort-${sortAsc ? "up" : "down"} ml-1`} style={{ fontSize: 10, color: "#6366f1" }} />;
  }

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-3 m-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
      {error}{" "}
      <button className="ml-2 text-xs font-medium text-slate-600 border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 transition-colors" onClick={load}>Retry</button>
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Agent Leaderboard</h2>
          <p style={{ margin: "4px 0 0", color: "var(--v-text-muted)", fontSize: 13 }}>
            Ranked agent performance — sortable by any metric
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rows.length > 0 && <MoshkyAnalyticsPanel reportName="Leaderboard" reportData={{ rows: rows.slice(0, 20), period, metric }} />}
          <button className="text-xs font-medium text-slate-600 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 transition-colors" onClick={load}>
            <i className="fa fa-refresh mr-1" /> Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {/* Period */}
        <div style={{ display: "flex", gap: 6 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              className={`text-xs px-2.5 py-1 rounded border font-medium transition-colors ${period === p.key ? "bg-primary text-white border-primary" : "text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              onClick={() => setPeriod(p.key)}
            >{p.label}</button>
          ))}
        </div>

        {/* Sort metric */}
        <select
          className="form-select form-select-sm"
          style={{ width: "auto" }}
          value={metric}
          onChange={e => setMetric(e.target.value)}
        >
          {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>

        {/* Team filter */}
        {teams.length > 0 && (
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={teamId}
            onChange={e => setTeamId(Number(e.target.value))}
          >
            <option value={0}>All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* Active Competitions badge strip */}
      {competitions.filter(c => c.isLive).length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {competitions.filter(c => c.isLive).map(c => (
            <span
              key={c.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                background: "#fef3c7",
                color: "#92400e",
                border: "1px solid #fde68a",
              }}
            >
              🏆 {c.name} — LIVE
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 24px", color: "var(--v-text-muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No data for this period</div>
          <p style={{ fontSize: 13 }}>
            No conversations found for the selected period and team filter.
          </p>
        </div>
      ) : (
        <div style={{ background: "var(--v-bg-card)", border: "1px solid var(--v-border)", borderRadius: 10, overflow: "hidden" }}>
          <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--v-bg-subtle)" }}>
                <th style={{ width: 60, paddingLeft: 16 }}>Rank</th>
                <th>Agent</th>
                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("totalChats")}>
                  Chats <SortIcon col="totalChats" />
                </th>
                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("resolutionRate")}>
                  Resolution % <SortIcon col="resolutionRate" />
                </th>
                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("avgFirstResponseSec")}>
                  First Response <SortIcon col="avgFirstResponseSec" />
                </th>
                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("avgCsatScore")}>
                  CSAT <SortIcon col="avgCsatScore" />
                </th>
                <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("qualityScore")}>
                  AI Quality <SortIcon col="qualityScore" />
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={row.agentId}>
                  <td style={{ paddingLeft: 16, fontWeight: 700, fontSize: 16 }}>
                    {medalIcon(i + 1)}
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {row.picture ? (
                        <img
                          src={row.picture}
                          alt={row.displayName}
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                          onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                          background: "#e0e7ff", display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#6366f1"
                        }}>
                          {row.displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontWeight: 600 }}>{row.displayName}</span>
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{row.totalChats}</td>
                  <td>
                    <span style={{ color: row.resolutionRate >= 80 ? "#22c55e" : row.resolutionRate >= 60 ? "#f59e0b" : "#ef4444", fontWeight: 600 }}>
                      {row.resolutionRate}%
                    </span>
                  </td>
                  <td style={{ color: row.avgFirstResponseSec > 0 && row.avgFirstResponseSec <= 60 ? "#22c55e" : row.avgFirstResponseSec > 300 ? "#ef4444" : "inherit" }}>
                    {fmtResponseTime(row.avgFirstResponseSec)}
                  </td>
                  <td>
                    {row.avgCsatScore >= 0 ? (
                      <span style={{ fontWeight: 600, color: row.avgCsatScore >= 4 ? "#22c55e" : row.avgCsatScore >= 3 ? "#f59e0b" : "#ef4444" }}>
                        {fmtCsat(row.avgCsatScore)}
                      </span>
                    ) : "—"}
                  </td>
                  <td>
                    {row.qualityScore != null ? (
                      <span style={{ fontWeight: 700, color: scoreColor(row.qualityScore), fontSize: 14 }}>
                        {row.qualityScore}
                        <span style={{ fontSize: 10, color: "var(--v-text-muted)", fontWeight: 400 }}> /100</span>
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Competitions section */}
      {competitions.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Active Competitions</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {competitions.map(c => (
              <div
                key={c.id}
                style={{
                  background: "var(--v-bg-card)",
                  border: `1px solid ${c.isLive ? "#fde68a" : "var(--v-border)"}`,
                  borderLeft: `4px solid ${c.isLive ? "#f59e0b" : "#94a3b8"}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: 22 }}>{c.isLive ? "🏆" : "🏅"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                  {c.description && (
                    <div style={{ fontSize: 12, color: "var(--v-text-muted)", marginTop: 2 }}>{c.description}</div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--v-text-muted)", textAlign: "right" }}>
                  <div>
                    {METRICS.find(m => m.key === c.metric)?.label ?? c.metric}
                  </div>
                  <div>
                    {new Date(c.startDate).toLocaleDateString()} – {new Date(c.endDate).toLocaleDateString()}
                  </div>
                </div>
                {c.isLive && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "2px 8px",
                    borderRadius: 12, background: "#fef3c7", color: "#92400e"
                  }}>LIVE</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
